<template>
  <main class="vote-record-page">
    <header class="vote-record-header">
      <button type="button" :aria-label="$t('frontend.common.back')" @click="back"><van-icon name="arrow-left" /></button>
      <h1>{{ $t('reservation.vote_record') }}</h1>
      <span class="vote-record-header__grid"><van-icon name="apps-o" /></span>
    </header>

    <div class="vote-record-tabs" role="tablist">
      <button v-for="tab in tabs" :key="tab.id" type="button" :class="{ 'is-active': activeTab === tab.id }" @click="activeTab = tab.id">{{ tab.label }}</button>
    </div>

    <van-pull-refresh v-model="refreshing" class="vote-record-refresh" @refresh="onRefresh">
      <van-list ref="orderList" v-model="loading" :finished="finished" :error.sync="loadError" :error-text="$t('common.response_error')" :finished-text="filteredRecords.length ? $t('video.no_more') : ''" :immediate-check="false" @load="load">
      <section class="vote-record-list">
        <article v-for="record in filteredRecords" :key="record.order_no || record.id" class="vote-record-card">
          <header>
            <strong>{{ $t('reservation.num') }} {{ record.expect }}</strong>
            <span :class="record.summary.settled ? 'is-drawn' : 'is-pending'">{{ $t(record.summary.statusKey) }}</span>
          </header>
          <p class="vote-record-card__order" :title="record.order_no || String(record.id)">{{ $t('reservation.order_number') }} #{{ record.id }}</p>
          <div class="vote-record-card__bets">
            <span>{{ $t('reservation.curr_choose') }}</span>
            <ul>
              <li v-for="(bet, index) in record.data" :key="bet.id || index"><b>{{ selectionOf(bet) }}</b><span>{{ bet.money }} {{ $t('reservation.unit') }}</span></li>
            </ul>
          </div>
          <div class="vote-record-card__detail">
            <span>{{ $t('reservation.voting_activity') }}</span>
            <div>
              <b v-for="(result, index) in resultsOf(record)" :key="index">{{ result }}</b>
              <strong v-if="record.summary.profit !== null" :class="'is-' + record.summary.outcome">{{ $t('reservation.' + record.summary.outcome) }} {{ formatPoints(record.summary.profit) }}</strong>
              <strong v-else-if="!record.summary.settled" class="is-pending">{{ $t(record.summary.statusKey) }}</strong>
              <strong v-else class="is-pending">{{ $t('common.response_error') }}</strong>
            </div>
          </div>
          <time>{{ record.create_time }}</time>
        </article>
        <p v-if="loaded && finished && !filteredRecords.length" class="vote-record-empty">{{ $t('frontend.common.empty') }}</p>
      </section>
      </van-list>
    </van-pull-refresh>
  </main>
</template>

<script>
import { summarizeVoteRecord } from '../../common/vote-record';

export default {
  data: () => ({ activeTab: 'all', records: [], refreshing: false, loaded: false, loading: false, finished: false, loadError: false, nextCursor: null, requestPending: false, requestVersion: 0, disposed: false }),
  computed: {
    tabs() {
      return [
        { id: 'all', label: this.$t('reservation.all') },
        { id: 'earned', label: this.$t('reservation.points_earned') },
        { id: 'pending', label: this.$t('reservation.to_be_drawn') }
      ];
    },
    summarizedRecords() {
      return this.records.map(record => ({ ...record, summary: summarizeVoteRecord(record) }));
    },
    filteredRecords() {
      if (this.activeTab === 'earned') return this.summarizedRecords.filter(record => record.summary.settled && record.summary.profit > 0);
      if (this.activeTab === 'pending') return this.summarizedRecords.filter(record => !record.summary.settled);
      return this.summarizedRecords;
    }
  },
  watch: {
    activeTab() {
      this.checkList();
    }
  },
  methods: {
    back() {
      this.$router.push('/Lottery');
    },
    formatPoints(value) {
      const points = Math.abs(value);
      return `${value > 0 ? '+' : value < 0 ? '-' : ''}${points} ${this.$t('reservation.unit')}`;
    },
    selectionOf(bet) {
      const labels = { big: 'vote_rose', small: 'vote_ship', one: 'vote_heart', two: 'vote_rocket' };
      return labels[bet.type] ? this.$t('reservation.' + labels[bet.type]) : String(bet.type == null ? '' : bet.type);
    },
    checkList() {
      this.$nextTick(() => {
        if (!this.disposed && this.$refs.orderList) this.$refs.orderList.check();
      });
    },
    resultsOf(record) {
      if (!record.summary.settled) return [];
      const values = Array.isArray(record.opencode) ? record.opencode.map(Number) : [];
      if (values.length === 3 && values.every(value => Number.isInteger(value) && value >= 1 && value <= 6)) {
        const sum = values.reduce((total, value) => total + value, 0);
        return [
          sum >= 11 ? this.$t('reservation.vote_rose') : this.$t('reservation.vote_ship'),
          sum % 2 ? this.$t('reservation.vote_heart') : this.$t('reservation.vote_rocket')
        ];
      }
      return [];
    },
    onRefresh() {
      // Ignore older requests that finish after this refresh starts.
      this.requestVersion += 1;
      this.requestPending = false;
      this.nextCursor = null;
      this.records = [];
      this.finished = false;
      this.loadError = false;
      this.loaded = false;
      return this.load();
    },
    async load() {
      if (this.requestPending || this.finished || this.disposed) return;
      const version = this.requestVersion;
      const data = { page_size: 20, group_by: 'order' };
      if (this.nextCursor !== null) data.cursor = this.nextCursor;
      this.requestPending = true;
      this.loading = true;
      this.loadError = false;
      try {
        const response = await this.$http({ method: 'get', url: 'user_get_game_list', data });
        if (this.disposed || version !== this.requestVersion) return;
        const page = response && response.data;
        if (!response || response.code !== 200 || !page || !Array.isArray(page.list) || typeof page.has_more !== 'boolean') {
          this.loadError = true;
          return;
        }
        const cursor = Number(page.next_cursor);
        if (page.has_more && (!Number.isFinite(cursor) || cursor <= 0 || (this.nextCursor !== null && cursor >= this.nextCursor))) {
          this.loadError = true;
          return;
        }
        const keyOf = record => String(record.order_no || record.id);
        const knownOrders = new Set(this.records.map(keyOf));
        page.list.filter(record => record && (record.order_no || record.id != null)).forEach(record => {
          const key = keyOf(record);
          if (!knownOrders.has(key)) {
            this.records.push(record);
            knownOrders.add(key);
          }
        });
        this.nextCursor = page.has_more ? cursor : null;
        this.finished = !page.has_more;
        this.loaded = true;
      } catch (error) {
        if (!this.disposed && version === this.requestVersion) this.loadError = true;
      } finally {
        if (!this.disposed && version === this.requestVersion) {
          this.requestPending = false;
          this.loading = false;
          this.refreshing = false;
          this.checkList();
        }
      }
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.finished = true;
      this.$router.push('/Login');
    }
    else this.load();
  },
  beforeDestroy() {
    this.disposed = true;
    this.requestVersion += 1;
  }
};
</script>

<style scoped>
.vote-record-page { min-height: 100vh; background: #f7f7fa; color: #282238; }
.vote-record-header { height: 3.06rem; display: grid; grid-template-columns: 3rem 1fr 3rem; align-items: center; background: #fff; }
.vote-record-header button { border: 0; background: transparent; color: #393040; font-size: 1.18rem; }
.vote-record-header h1 { margin: 0; font-size: 1rem; font-weight: 700; text-align: center; }
.vote-record-header__grid { display: flex; align-items: center; justify-content: center; color: #554864; font-size: 1.18rem; }
.vote-record-tabs { display: grid; grid-template-columns: repeat(3, 1fr); height: 2.8rem; background: #fff; border-top: 0.04rem solid #f0edf3; border-bottom: 0.04rem solid #e8e5eb; }
.vote-record-tabs button { border: 0; border-bottom: 0.12rem solid transparent; color: #655d6d; background: #fff; font-size: 0.84rem; }
.vote-record-tabs button.is-active { color: #8343b4; border-bottom-color: #8343b4; font-weight: 700; }
.vote-record-list { padding: 0.65rem; }
.vote-record-card { min-height: 8.1875rem; margin-bottom: 0.75rem; padding: 0.8rem; border-radius: 0; background: #fff; }
.vote-record-card header, .vote-record-card__detail { display: flex; justify-content: space-between; gap: 0.8rem; }
.vote-record-card header { font-size: 0.86rem; }
.vote-record-card header span { font-size: 0.78rem; }
.vote-record-card__order { margin: 0.45rem 0; color: #928b99; font-size: 0.76rem; }
.vote-record-card__bets { display: flex; justify-content: space-between; gap: 0.8rem; margin-top: 0.7rem; color: #57505e; font-size: 0.84rem; }
.vote-record-card__bets ul { flex: 1; min-width: 0; margin: 0; padding: 0; list-style: none; }
.vote-record-card__bets li { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 0.4rem 0.8rem; margin-bottom: 0.3rem; }
.vote-record-card__bets b { color: #7544a6; font-weight: 700; }
.vote-record-card .is-drawn { color: #2e9b62; }
.vote-record-card .is-pending { color: #c0783e; }
.vote-record-card__detail { align-items: center; margin-top: 0.62rem; min-height: 2.6rem; color: #57505e; font-size: 0.84rem; }
.vote-record-card__detail > div { display: flex; flex-direction: column; align-items: flex-end; gap: 0.26rem; }
.vote-record-card__detail b { color: #7544a6; font-size: 0.8rem; font-weight: 700; }
.vote-record-card__detail strong { font-size: 0.82rem; }
.vote-record-card .is-win { color: #2e9b62; }
.vote-record-card .is-lose { color: #c85665; }
.vote-record-card .is-break_even { color: #655d6d; }
.vote-record-card time { display: block; margin-top: 0.55rem; color: #928b99; font-size: 0.76rem; }
.vote-record-empty { padding: 3rem 0; color: #918b98; font-size: 0.85rem; text-align: center; }
</style>
